import isEmail from 'is-email-maybe';
import * as React from 'react';

import {
    createPalette,
    type PaletteDef,
    TagInput,
    type TagInputProps,
    type TagInputRef,
    usePaletteHandle,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { useDebouncedState } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';

import { apiResource, readAPICache } from '../../state';
import { searchOrganizationMembers } from '../Search';
import type { PermissionTag } from '../ShareContent/InviteMembers/state';
import { UserAvatar } from './UserAvatar';

type Props = Omit<
    TagInputProps<PermissionTag>,
    | 'onChangeValue'
    | 'ref'
    | 'wrapperRef'
    | 'text'
    | 'onChangeText'
    | 'onEnterTag'
    | 'renderTag'
    | 'tagDelimiters'
> & {
    organization: string;

    /**
     * If true, the input allows emails which will send
     * invites (as guest users).
     *
     * Defaults to true.
     */
    canInviteUsers?: boolean;

    /**
     * If true, teams are shown.
     *
     * Defaults to true.
     */
    showTeams?: boolean;

    onChangeValue: (
        tags: PermissionTag[] | ((previousTags: PermissionTag[]) => PermissionTag[])
    ) => void;
};

export type UserTagInputRef = {
    /**
     * Reset the tag input.
     */
    reset: () => void;
};

/**
 * Generic input for searching users, teams or emails
 */
export function UserTagInput({
    ref,
    ...props
}: Props & { ref?: React.Ref<UserTagInputRef> }): React.ReactElement {
    const {
        canInviteUsers = true,
        showTeams = true,
        organization,
        value,
        onChangeValue,
        ...tagInputProps
    } = props;

    const [text, debouncedText, setText] = useDebouncedState('');
    const anchorRef = React.useRef<TagInputRef>(null);
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const paletteHandle = usePaletteHandle(wrapperRef, { inputRef: anchorRef });

    // Auto-focus when opening
    React.useEffect(() => {
        anchorRef.current?.focus();
    }, []);

    // Update autocomplete when user is typing
    React.useEffect(() => {
        if (!text) {
            paletteHandle.close();
            return;
        }

        if (!paletteHandle.opened) {
            paletteHandle.open(
                createAutocompletePalette(
                    (item) => {
                        onChangeValue((prevTags) => [...prevTags, item]);
                        setText('');
                        paletteHandle.close();
                        anchorRef.current?.focus();
                    },
                    organization,
                    canInviteUsers,
                    showTeams
                )
            );
        }
        paletteHandle.typeQuery(debouncedText);
    }, [text, organization, debouncedText]);

    // User pressed enter on a text.
    const onEnterTag = () => {
        setText('');
    };

    React.useImperativeHandle(
        ref,
        () => ({
            reset: () => {
                setText('');
            },
        }),
        [setText]
    );

    const placeholder = (() => {
        if (canInviteUsers && showTeams) {
            return 'Add someone, a team, or an email';
        }

        if (canInviteUsers) {
            return 'Add someone or an email';
        }

        if (showTeams) {
            return 'Add someone or a team';
        }

        return 'Add someone';
    })();

    return (
        <TagInput
            ref={anchorRef}
            wrapperRef={wrapperRef}
            placeholder={placeholder}
            value={value}
            text={text}
            onChangeText={setText}
            onChangeValue={onChangeValue}
            onEnterTag={onEnterTag}
            renderTag={renderTag}
            tagDelimiters={['Enter', 'Tab']}
            className="flex-1"
            {...tagInputProps}
        />
    );
}

/**
 * Render a tag.
 */
function renderTag(item: PermissionTag): string {
    switch (item.type) {
        case 'email':
            return item.email;
        case 'user':
            return item.displayName;
        case 'team':
            return item.title;
        default:
            assertNever(item);
    }
}

/**
 * Create the palette to autocomplete users/teams.
 */
function createAutocompletePalette(
    pick: (item: PermissionTag) => any,
    organization: string,
    canInviteUsers: boolean,
    showTeams: boolean
): PaletteDef {
    const searchMembers = (query: string) =>
        searchOrganizationMembers({
            organizationId: organization,
            query,
            limit: 10,
        });

    return createPalette({
        groups: [
            {
                key: 'email',
                commands: (query) => {
                    if (!canInviteUsers || !isEmail(query)) {
                        return [];
                    }

                    const members = searchMembers(query);

                    if (
                        members.some(
                            (member) => member.user.email?.toLowerCase() === query.toLowerCase()
                        )
                    ) {
                        return [];
                    }

                    return [
                        {
                            key: query,
                            title: query,
                            icon: Icon.Email,
                            trigger: () => {
                                pick({ type: 'email', email: query });
                            },
                        },
                    ];
                },
            },
            {
                key: 'users',
                commands: (query) => {
                    const members = searchMembers(query);

                    return members.map((member) => {
                        const { user: memberUser } = member;
                        return {
                            key: memberUser.id,
                            icon: () => <UserAvatar user={memberUser} size="sm" tooltip={false} />,
                            title: memberUser.displayName,
                            subtitle: memberUser.email,
                            trigger: () => {
                                pick({
                                    type: 'user',
                                    displayName: memberUser.displayName,
                                    user: memberUser.id,
                                });
                            },
                        };
                    });
                },
            },
            {
                key: 'teams',
                commands: (query) => {
                    if (!showTeams) {
                        return [];
                    }

                    const { items } = readAPICache(
                        apiResource('listTeamsInOrganizationById', {
                            organizationId: organization,
                            limit: 10,
                            title: query,
                        })
                    );

                    return items.map((team) => ({
                        key: team.id,
                        icon: Icon.Users,
                        title: team.title,
                        trigger: () =>
                            pick({
                                type: 'team',
                                title: team.title,
                                organization,
                                team: team.id,
                            }),
                    }));
                },
            },
        ],
    });
}
